import getConfig from 'next/config';

import Service from './service';
import { getActivePushSubscriptions, updateUserRating } from './userService';

const {
  publicRuntimeConfig: { HttpRoutes, PUSH_NOTIFICATIONS_VAPID_KEY },
} = getConfig();

export const currentSubscriptionLocalStorageKey = 'currentPushSubscription';

export const getSavedSubscription = () => {
  const subscription = window.localStorage.getItem(currentSubscriptionLocalStorageKey);
  if (subscription) {
    try {
      return JSON.parse(window.localStorage.getItem(currentSubscriptionLocalStorageKey));
    } catch (err) {
      window.localStorage.removeItem(currentSubscriptionLocalStorageKey);
      return null;
    }
  } else {
    return null;
  }
};

/**
 * Subscribe/Unsubscribe for push subscriptions
 * @param {string} userId User Id
 * @param {'subscribe' | 'unsubscribe'} action Action to perform
 * @param {PushSubscription} subscription Push Subscription
 * @param {boolean} expired Boolean flag indicating whether the subscription is expired or not
 * @param {boolean} logActivity Boolean flag indicating whether to log PUSH_ACTIVITY with corresponding pushActivityType
 * @returns
 */
export const pushSubscriptionNotification = (
  userId,
  action,
  subscription,
  expired = false,
  logActivity = true
) => {
  if (!userId || !subscription) {
    return Promise.reject('Provide mandatory arguments :: subscription and userId');
  }

  if (action === 'subscribe') {
    // Save newSubscription to the localstorage
    localStorage.setItem(currentSubscriptionLocalStorageKey, JSON.stringify(subscription));
  } else if (action === 'unsubscribe') {
    localStorage.removeItem(currentSubscriptionLocalStorageKey);
  } else {
    return Promise.reject(
      'Unknown subscription action. Should be between ["subscribe", "unsubscribe"]'
    );
  }

  const data = {
    userId,
    action,
    subscription,
    logActivity,
    url: window.location.href,
  };
  if (expired) data.expired = expired;
  return new Promise((resolve, reject) => {
    Service.put(HttpRoutes.pushSubscriptionNotification, data)
      .then((response) => resolve(response.data))
      .catch((err) => reject(err));
  });
};

export const registerServiceWorker = () => {
  if ('serviceWorker' in navigator) {
    if ('PushManager' in window) {
      return navigator.serviceWorker.register('/service-worker.js');
    } else {
      console.error('push manager is not supported');
    }
  } else {
    console.error("Browser doesn't support service worker");
  }
};

/**
 * Get a new subcription from PushManager
 * @returns {Promise<PushSubscription>}
 */
export const getSubscription = () => {
  return new Promise((resolve, reject) => {
    navigator.serviceWorker
      .getRegistration()
      .then((registration) => {
        if (registration) return registration.pushManager.getSubscription();
        else {
          registerServiceWorker()
            .then((registration) => registration.pushManager.getSubscription())
            .catch((err) => reject(err));
        }
      })
      .then((subscription) => {
        resolve(subscription);
      })
      .catch((err) => {
        reject(err);
      });
  });
};

export const addTitleAndLocation = (criteria = {}) => {
  if (criteria.title) {
    // Add title to userRatings
    updateUserRating({ title: criteria.title });
  }
  if (criteria.geoState || criteria.geoCity) {
    // Add geo-location to userRatings
    updateUserRating({
      city: criteria.geoCity,
      stateAbbr: criteria.geoStateCode,
      state: criteria.geoState,
    });
  }
};

/**
 *
 * @param {import('types').User} user
 * @returns
 */
export const startPollingForPushSubscriptionChange = async (user) => {
  // Don't even look forward if the permission is denied
  if (typeof window.Notification === 'undefined' || window.Notification.permission !== 'granted')
    return;

  /**
   * Fetch the saved old subscription from local storage
   * @type {PushSubscription}
   */
  const savedSubscription = getSavedSubscription();

  let newSubscription;

  // Fetch the new subscription from push manager
  try {
    newSubscription = (await getSubscription())?.toJSON();
  } catch (err) {
    console.error(err);
    return;
  }

  if (!newSubscription) {
    return;
  }

  let pushSubscriptions = [];

  try {
    pushSubscriptions = await getActivePushSubscriptions();
  } catch (err) {
    console.error(err);
  }

  const newSubscriptionExists = pushSubscriptions.some((pushSubscription) => {
    return (
      pushSubscription.subscription &&
      pushSubscription.subscription.endpoint === newSubscription.endpoint
    );
  });

  if(newSubscriptionExists) {
    // Save newSubscription to the localstorage
    return localStorage.setItem(currentSubscriptionLocalStorageKey, JSON.stringify(newSubscription));
  }

  if (savedSubscription) {
    const savedSubscriptionExists = pushSubscriptions.some((pushSubscription) => {
      return (
        pushSubscription.subscription &&
        pushSubscription.subscription.endpoint === savedSubscription.endpoint
      );
    });

    // Check if the endpoint of the old and new subscription is same or not
    if (
      savedSubscriptionExists &&
      newSubscription.endpoint &&
      savedSubscription.endpoint &&
      newSubscription.endpoint !== savedSubscription.endpoint
    ) {
      // This means subscription for the user might have changed

      // Unsubscribe from the old/expired/malformed subscription
      pushSubscriptionNotification(user.userId, 'unsubscribe', savedSubscription, true);

      // Subscribe for the new subscription
      pushSubscriptionNotification(user.userId, 'subscribe', newSubscription, false);
    } else {
      // Else update new subscribe to DB always
      pushSubscriptionNotification(user.userId, 'subscribe', newSubscription, false);
    }
  } else {
    if (pushSubscriptions && pushSubscriptions.length) {
      const unsubscribes = [];
      pushSubscriptions.forEach((pushSubscription) => {
        // Unsubscribe from the subscription saved in DB
        if (pushSubscription.subscription)
          unsubscribes.push(
            pushSubscriptionNotification(
              user.userId,
              'unsubscribe',
              pushSubscription.subscription,
              false
            )
          );
      });
      Promise.all(unsubscribes).catch((err) => {
        console.error('Error occurred while unsubscribing all push notification', err);
      });
    }
    // Subscribe a new subscription
    pushSubscriptionNotification(user.userId, 'subscribe', newSubscription, false);
  }
};

/**
 * Fetch all the active subscriptions for user from API and
 * return the subscription hash for the current device to callee
 * @param {PushSubscription} existingSubscription
 * @returns {Promise<string> | Promise<undefined} A promise of subscription hash
 */
export const fetchCurrentSubscriptionHash = async (existingSubscription) => {
  if (typeof window.Notification === 'undefined' || window.Notification.permission !== 'granted')
    return Promise.resolve();
  try {
    /**
     * @type {{ hash: string, subscription: PushSubscription }[]}
     */
    const subscriptionHashes = await getActivePushSubscriptions();
    if (subscriptionHashes?.length && existingSubscription) {
      // find the hash and return it
      const subscription = subscriptionHashes.find(
        (hash) => hash.subscription.endpoint === existingSubscription.endpoint
      );
      if (subscription) {
        return subscription.hash;
      }
    }
  } catch (err) {
    console.error(err);
  }
};

export const subscribeToPushNotifications = (
  pushNotificationVapidKey = PUSH_NOTIFICATIONS_VAPID_KEY
) => {
  return navigator.serviceWorker.ready
    .then((registration) => {
      return registration.pushManager
        .subscribe({
          userVisibleOnly: true,
          applicationServerKey: pushNotificationVapidKey,
        })
        .then((subscription) => {
          return subscription;
        })
        .catch((err) => {
          throw err;
        });
    })
    .catch((error) => {
      throw error;
    });
};

export const unSubscribeToPushNotifications = () => {
  return navigator.serviceWorker.ready
    .then((registration) => {
      return registration.pushManager
        .getSubscription()
        .then((subscription) => {
          if (subscription) {
            return subscription
              .unsubscribe()
              .then(() => subscription)
              .catch((err) => {
                throw err;
              });
          } else {
            throw new Error('Unable to unsubscribe');
          }
        })
        .catch((err) => {
          throw err;
        });
    })
    .catch((err) => {
      throw err;
    });
};
